<template>
    <div class="map-wrapper body">

    <div class="map-search" v-if="showSearchBox">

        <input
        v-model="searchTerm"
        @input="handleSearch"
        placeholder="جستجوی مکان..."
        class="search-input"
        />

        <div v-if="results.length" class="search-results">

        <div
            v-for="item in results"
            :key="item.title + item.address"
            class="result-item"
            @click="selectLocation(item)"
        >
            <strong>{{ item.title }}</strong>
            <small>{{ item.address }}</small>
        </div>

        </div>

    </div>

    <div ref="mapContainer" :style="{ height: height, width: width }"></div>

    </div>

</template>

<script setup>
import { onMounted, ref, watch, defineExpose, defineProps } from "vue";
import nmp from "@neshan-maps-platform/mapbox-gl";
import { MapService } from "@/services/api"

const searchTerm = ref("")
const results = ref([])
const mapReady = ref(false)

let searchTimeout = null

async function handleSearch(){

  if(searchTimeout){
    clearTimeout(searchTimeout)
  }

  searchTimeout = setTimeout(async () => {

    if(searchTerm.value.length < 2){
      results.value = []
      return
    }

    try{

      if(!mapReady.value) return

      const center = map.getCenter()


      const data = await MapService.searchLocation(
        searchTerm.value,
        center.lat,
        center.lng
      )

      results.value = data.items || []

    }catch(err){
      console.error(err)
    }

  },400)

}

function selectLocation(item){

  const lng = item.location.x
  const lat = item.location.y

  map.flyTo({
    center:[lng,lat],
    zoom:16
  })

  results.value = []
  searchTerm.value = item.title

}


const props = defineProps({
  apiKey: {
    type: String,
    required: false,
    default: 'web.73a02790728f4a38862451622ccb92d7'
  },
  center: {
    type: Array,
    default: () => [51.4231, 35.6892],
  },
  zoom: {
    type: Number,
    default: 12,
  },
  height: {
    type: Number,
    default: 12,
  },
  width: {
    type: Number,
    default: 12,
  },
  markers: {
    type: Array,
    default: () => [],
  },
  mapType: {
    type: String,
    default: "neshan", 
  },
showSearchBox: {
    type: Boolean,
    default: true
  }
});

const mapContainer = ref(null);
let map = null;
let markerInstances = [];

onMounted(() => {
    initializeMap();
});

watch(() => [props.center, props.zoom, props.markers, props.mapType], (newValue, oldValue) => {
  const [newCenter, newZoom, newMarkers, newMapType] = newValue;
  const [oldCenter, oldZoom, oldMarkers, oldMapType] = oldValue;


  if (!map) {
    console.warn("Map not initialized yet, skipping prop updates.");
    return; 
  }

  if (
    JSON.stringify(newCenter) !== JSON.stringify(oldCenter) ||
    newZoom !== oldZoom
  ) {
    map.setCenter(newCenter);
    map.setZoom(newZoom);
  }

  if (newMapType !== oldMapType) {
      map.setStyle(newMapType === 'neshan' ? 'neshan://styles/basic' : `/${newMapType}`); 
      console.log(`Map type changed to: ${newMapType}`);
      updateMarkers();
  }


  if (newMarkers !== oldMarkers) {
    updateMarkers();
  }

});

function initializeMap() {
  if (!mapContainer.value) {
    console.error("Map container not found!");
    return;
  }

  if (!props.apiKey) {
      console.error("Neshan API key is missing!");
      return;
  }

  try {
      if (typeof nmp === 'undefined') {
          console.error("Neshan Mapbox GL (nmp) library is not loaded or available.");
          return;
      }

      map = new nmp.Map({
        mapKey: props.apiKey,
        container: mapContainer.value,
        center: props.center,
        zoom: props.zoom,
        mapType: props.mapType, 
      });

      map.on("load", () => {
        console.log("Neshan map loaded successfully!");
        mapReady.value = true
        addMarkersToMap();
      });

      map.on("error", (e) => {
        console.error("Neshan map error:", e);
      });


  } catch (error) {
      console.error("Error initializing Neshan map:", error);
  }
}

function addMarkersToMap() {
  if (!map) {
      console.log("Map not initialized yet, skipping marker add.");
      return;
  }
  if (typeof nmp === 'undefined') {
      console.error("Neshan Mapbox GL (nmp) library is not loaded or available.");
      return;
  }
  if (!props.markers || props.markers.length === 0) {
      console.log("No markers provided or markers array is empty, skipping marker add.");
      removeMarkersFromMap();
      return;
  }

  console.log(`Attempting to add ${props.markers.length} markers to map...`); 
  props.markers.forEach((markerData, index) => {
    if (typeof markerData.lng === 'undefined' || typeof markerData.lat === 'undefined') {
        console.warn(`Skipping marker ${index}: Invalid coordinates provided.`);
        return; // Skip this marker if coordinates are missing
    }

    try {
        const marker = new nmp.Marker({
        })
          .setLngLat([markerData.lng, markerData.lat])
          .addTo(map);

        if (markerData.label) {
          const popup = new nmp.Popup({
            offset: 25, 
          }).setText(markerData.label);
          marker.setPopup(popup);
        }
        markerInstances.push(marker); 
    } catch (markerError) {
        console.error(`Error adding marker ${index} at [${markerData.lng}, ${markerData.lat}]:`, markerError);
    }
  });
  console.log(`Finished processing. ${markerInstances.length} marker instances created.`);
}

function removeMarkersFromMap() {
  if (markerInstances.length > 0) {
    console.log(`Removing ${markerInstances.length} existing markers.`);
    markerInstances.forEach(marker => {
      marker.remove();
    });
    markerInstances = [];
  }
}

function updateMarkers() {
    console.log("Updating markers...");
    removeMarkersFromMap();
    addMarkersToMap(); 
}

function getCenter(){

  if(!map || !mapReady.value){
    console.warn("Map not ready yet")
    return null
  }

  const c = map.getCenter()

  return {
    lng: c.lng,
    lat: c.lat
  }

}
function getMap(){
  return map
}

defineExpose({
  getMap,
  setCenter: (newCenter) => {
    if (map) {
      map.setCenter(newCenter);
    }
  },
  setZoom: (newZoom) => {
    if (map) {
      map.setZoom(newZoom);
    }
  },
  addMarker: (markerData) => {
      console.warn("Directly adding a single marker might require re-initializing or careful state management. Consider updating the 'markers' prop instead.");
      console.log(markerData)
  },
  removeMarkers: removeMarkersFromMap,
  getCenter
});

</script>

<style scoped>
.map-wrapper{
position:relative;
height:100%;
width:100%;
}

.map-search{
position:absolute;
top:10px;
left:50%;
transform:translateX(-50%);
z-index:20;
width:350px;
}

.search-input{
width:100%;
padding:10px;
border-radius:8px;
border:1px solid #ddd;
}

.search-results{
background:white;
border-radius:8px;
margin-top:6px;
max-height:300px;
overflow:auto;
box-shadow:0 4px 12px rgba(0,0,0,0.15);
}

.result-item{
padding:10px;
cursor:pointer;
border-bottom:1px solid #eee;
}

.result-item:hover{
background:#f3f3f3;
}
</style>
